import type { UniAuthConfig, TokenSet, UserInfo, IdTokenClaims, LoginOptions } from "./types"
import { generateCodeVerifier, generateCodeChallenge, generateState } from "./crypto"
import { fetchDiscovery, normalizeIssuer } from "./discovery"
import { UniAuthError, AuthorizationError, TokenError, NetworkError, TimeoutError } from "./errors"
import { createStorage, type TokenStorage } from "./storage"
import { AuthSync, type SyncEventType, type SyncHandler } from "./sync"
import { verifyIdToken as verifyIdTokenJwks } from "./jwks"
import { fetchWithRetry } from "./retry"

export type { UniAuthConfig, TokenSet, UserInfo, IdTokenClaims, LoginOptions } from "./types"
export type { TokenStorage } from "./storage"
export type { SyncEventType, SyncHandler } from "./sync"
export { UniAuthError, AuthorizationError, TokenError, NetworkError, TimeoutError } from "./errors"
export { createStorage, MemoryStorageAdapter, SessionStorageAdapter, LocalStorageAdapter } from "./storage"
export { AuthSync } from "./sync"
export { verifyIdToken } from "./jwks"
export { fetchWithRetry } from "./retry"

const STORAGE_PREFIX = "uniauth_"
const LEGACY_STORAGE_KEYS = {
  tokens: `${STORAGE_PREFIX}tokens`,
  state: `${STORAGE_PREFIX}state`,
  codeVerifier: `${STORAGE_PREFIX}code_verifier`,
  nonce: `${STORAGE_PREFIX}nonce`,
} as const

const RESERVED_AUTHORIZATION_PARAMS = new Set([
  "response_type",
  "client_id",
  "redirect_uri",
  "scope",
  "state",
  "nonce",
  "code_challenge",
  "code_challenge_method",
  "request",
  "request_uri",
  "response_mode",
  "prompt",
  "login_hint",
  "max_age",
  "acr_values",
  "organization",
])

function createStoragePrefix(issuer: string, clientId: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(normalizeIssuer(issuer))}:${encodeURIComponent(clientId)}:`
}

function decodeJwtPayload(token: unknown): Record<string, unknown> | null {
  if (typeof token !== "string") return null
  const parts = token.split(".")
  if (parts.length !== 3) return null

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")
    const payload = JSON.parse(atob(padded))
    return payload && typeof payload === "object" ? payload : null
  } catch {
    return null
  }
}

export class UniAuth {
  private config: Required<Pick<UniAuthConfig, "issuer" | "clientId" | "redirectUri">> & UniAuthConfig
  private tokens: TokenSet | null = null
  private storage: TokenStorage
  private sync: AuthSync | null = null
  private _idTokenClaims: IdTokenClaims | null = null
  private retryOptions: { maxRetries: number } | null = null
  private storagePrefix: string

  constructor(config: UniAuthConfig) {
    if (!config.issuer) throw new UniAuthError("configuration_error", "issuer is required")
    if (!config.clientId) throw new UniAuthError("configuration_error", "clientId is required")
    if (!config.redirectUri) throw new UniAuthError("configuration_error", "redirectUri is required")
    this.config = { ...config, issuer: normalizeIssuer(config.issuer) }
    this.storagePrefix = createStoragePrefix(this.config.issuer, config.clientId)

    this.storage = createStorage(config.storage || "sessionStorage")

    if (config.retry) {
      this.retryOptions = typeof config.retry === "object"
        ? { maxRetries: config.retry.maxRetries ?? 3 }
        : { maxRetries: 3 }
    }

    if (config.crossTabSync !== false && typeof window !== "undefined") {
      this.sync = new AuthSync(`${this.storagePrefix}sync`, () => {
        // Default handler reloads tokens from storage
        this.loadTokens()
      })
    }

    this.loadTokens()
  }

  async login(options?: LoginOptions): Promise<void> {
    const discovery = await this.fetchWithOptionalRetry(
      () => fetchDiscovery(this.config.issuer)
    )
    const state = generateState()
    const codeVerifier = generateCodeVerifier()
    const codeChallenge = await generateCodeChallenge(codeVerifier)

    const nonceArray = new Uint8Array(16)
    crypto.getRandomValues(nonceArray)
    const nonce = Array.from(nonceArray, (b) => b.toString(16).padStart(2, "0")).join("")

    this.storage.set(this.storageKey("state"), state)
    this.storage.set(this.storageKey("code_verifier"), codeVerifier)
    this.storage.set(this.storageKey("nonce"), nonce)

    const scopes = options?.scope
      ? options.scope.split(" ")
      : (this.config.scopes || ["openid", "profile", "email"])

    const params = new URLSearchParams({
      response_type: "code",
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      scope: scopes.join(" "),
      state,
      nonce,
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    })

    if (options?.prompt) params.set("prompt", options.prompt)
    if (options?.loginHint) params.set("login_hint", options.loginHint)
    if (options?.maxAge !== undefined) params.set("max_age", String(options.maxAge))

    if (options?.acrValues) {
      const acr = Array.isArray(options.acrValues)
        ? options.acrValues.join(" ")
        : options.acrValues
      params.set("acr_values", acr)
    }

    const org = options?.organization || this.config.organization
    if (org) params.set("organization", org)

    // Merge custom params: config-level first, then call-level overrides
    const merged = { ...this.config.customParams, ...options?.customParams }
    for (const [key, value] of Object.entries(merged)) {
      if (RESERVED_AUTHORIZATION_PARAMS.has(key)) continue
      params.set(key, value)
    }

    const mode = options?.mode || this.detectMode()

    if (mode === "popup") {
      return this.loginPopup(
        `${discovery.authorization_endpoint}?${params.toString()}`,
        options?.popupWidth || 480,
        options?.popupHeight || 640
      )
    }

    window.location.href = `${discovery.authorization_endpoint}?${params.toString()}`
  }

  /**
   * Detect whether to use popup or redirect.
   * Popup on desktop browsers, redirect on mobile/tablet or when popups are likely blocked.
   */
  private detectMode(): "redirect" | "popup" {
    if (typeof window === "undefined") return "redirect"
    const ua = navigator.userAgent
    const isMobile = /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua)
    return isMobile ? "redirect" : "popup"
  }

  /**
   * Open authorization in a popup window, wait for callback via postMessage.
   * Returns a Promise that resolves when auth completes or rejects if the popup is closed.
   */
  private loginPopup(authUrl: string, width: number, height: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const left = Math.round(window.screenX + (window.outerWidth - width) / 2)
      const top = Math.round(window.screenY + (window.outerHeight - height) / 2)
      const features = `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`

      const popup = window.open(authUrl, "uniauth_popup", features)
      if (!popup) {
        // Popup blocked — fall back to redirect
        window.location.href = authUrl
        return
      }

      // Listen for the callback page to postMessage us the code+state
      const onMessage = async (event: MessageEvent) => {
        if (event.data?.type !== "uniauth_callback") return
        // Validate origin matches our issuer or redirect URI
        const allowedOrigins = [
          new URL(this.config.issuer).origin,
          new URL(this.config.redirectUri).origin,
        ]
        if (!allowedOrigins.includes(event.origin)) return

        cleanup()
        popup.close()

        const { code, state, error, error_description } = event.data

        if (error) {
          reject(new AuthorizationError(error, error_description || error))
          return
        }

        // Manually inject code+state into the URL so handleCallback can process them
        const callbackUrl = `${this.config.redirectUri}?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`
        window.history.replaceState({}, "", callbackUrl)
        try {
          await this.handleCallback()
          resolve()
        } catch (err) {
          reject(err)
        }
      }

      // Poll for popup closed (user dismissed it)
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          cleanup()
          reject(new UniAuthError("popup_closed", "Authentication popup was closed"))
        }
      }, 500)

      const cleanup = () => {
        clearInterval(pollTimer)
        window.removeEventListener("message", onMessage)
      }

      window.addEventListener("message", onMessage)
    })
  }

  async handleCallback(): Promise<TokenSet> {
    const params = new URLSearchParams(window.location.search)
    const code = params.get("code")
    const state = params.get("state")
    const error = params.get("error")

    if (error) {
      const desc = params.get("error_description") || error
      throw new AuthorizationError(error, desc)
    }

    if (!code || !state) {
      throw new AuthorizationError("invalid_callback", "Missing code or state in callback URL")
    }

    const scopedState = this.storage.get(this.storageKey("state"))
    const legacyState = scopedState === null
      ? this.storage.get(LEGACY_STORAGE_KEYS.state)
      : null
    const usesLegacyTransaction = scopedState === null && legacyState === state
    const savedState = scopedState ?? legacyState
    if (state !== savedState) {
      throw new AuthorizationError("state_mismatch", "State mismatch — possible CSRF attack")
    }

    const codeVerifier = this.storage.get(
      usesLegacyTransaction ? LEGACY_STORAGE_KEYS.codeVerifier : this.storageKey("code_verifier")
    )
    if (!codeVerifier) {
      throw new AuthorizationError("missing_verifier", "Missing code_verifier — was the login flow started properly?")
    }

    const savedNonce = this.storage.get(
      usesLegacyTransaction ? LEGACY_STORAGE_KEYS.nonce : this.storageKey("nonce")
    )

    if (usesLegacyTransaction) {
      this.storage.remove(LEGACY_STORAGE_KEYS.state)
      this.storage.remove(LEGACY_STORAGE_KEYS.codeVerifier)
      this.storage.remove(LEGACY_STORAGE_KEYS.nonce)
    } else {
      this.storage.remove(this.storageKey("state"))
      this.storage.remove(this.storageKey("code_verifier"))
      this.storage.remove(this.storageKey("nonce"))
    }

    let discovery
    try {
      discovery = await this.fetchWithOptionalRetry(
        () => fetchDiscovery(this.config.issuer)
      )
    } catch (err) {
      throw new NetworkError(`Failed to fetch OIDC discovery: ${err instanceof Error ? err.message : String(err)}`)
    }

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: this.config.redirectUri,
      client_id: this.config.clientId,
      code_verifier: codeVerifier,
    })

    let res: Response
    try {
      res = await this.doFetch(discovery.token_endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      })
    } catch (err) {
      throw new NetworkError(`Token request failed: ${err instanceof Error ? err.message : String(err)}`)
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "token_error", error_description: res.statusText }))
      throw new TokenError(err.error || "token_error", err.error_description || res.statusText)
    }

    const tokenResponse = await res.json()

    // ID token verification
    if (tokenResponse.id_token) {
      if (this.config.verifyIdToken !== false) {
        try {
          this._idTokenClaims = await verifyIdTokenJwks(
            tokenResponse.id_token,
            discovery.jwks_uri,
            this.config.issuer.replace(/\/$/, ""),
            this.config.clientId,
            savedNonce || undefined
          )
        } catch (err) {
          throw new TokenError(
            "id_token_verification_failed",
            `ID token verification failed: ${err instanceof Error ? err.message : String(err)}`
          )
        }
      } else if (savedNonce) {
        // Fallback: nonce-only validation (v1 behavior when verification disabled)
        try {
          const payload = JSON.parse(atob(tokenResponse.id_token.split(".")[1]))
          if (payload.nonce !== savedNonce) {
            throw new AuthorizationError("nonce_mismatch", "ID token nonce does not match — possible replay attack")
          }
          this._idTokenClaims = payload as IdTokenClaims
        } catch (err) {
          if (err instanceof AuthorizationError) throw err
          // If we can't decode the ID token, skip nonce validation
        }
      }
    }

    this.tokens = {
      ...tokenResponse,
      expires_at: Date.now() + tokenResponse.expires_in * 1000,
    }

    this.saveTokens()
    this.sync?.emit("login")

    // Clean URL
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname)
    }

    return this.tokens!
  }

  async getUser(): Promise<UserInfo> {
    const accessToken = await this.getAccessToken()
    if (!accessToken) throw new UniAuthError("not_authenticated", "Not authenticated")

    const discovery = await this.fetchWithOptionalRetry(
      () => fetchDiscovery(this.config.issuer)
    )
    let res: Response
    try {
      res = await this.doFetch(discovery.userinfo_endpoint, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
    } catch (err) {
      throw new NetworkError(`UserInfo request failed: ${err instanceof Error ? err.message : String(err)}`)
    }

    if (res.status === 401) {
      const refreshed = await this.refreshToken()
      if (!refreshed) throw new UniAuthError("session_expired", "Session expired — please log in again")

      const retry = await this.doFetch(discovery.userinfo_endpoint, {
        headers: { Authorization: `Bearer ${this.tokens!.access_token}` },
      })
      if (!retry.ok) throw new NetworkError("Failed to fetch user info after token refresh")
      return retry.json()
    }

    if (!res.ok) throw new NetworkError("Failed to fetch user info")
    return res.json()
  }

  async refreshToken(): Promise<TokenSet | null> {
    if (!this.tokens?.refresh_token) return null

    const discovery = await this.fetchWithOptionalRetry(
      () => fetchDiscovery(this.config.issuer)
    )
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: this.tokens.refresh_token,
      client_id: this.config.clientId,
    })

    const res = await this.doFetch(discovery.token_endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    })

    if (!res.ok) {
      this.clearTokens()
      return null
    }

    const tokenResponse = await res.json()
    this.tokens = {
      ...tokenResponse,
      expires_at: Date.now() + tokenResponse.expires_in * 1000,
    }
    this.saveTokens()
    this.sync?.emit("token_refresh")
    return this.tokens
  }

  async logout(): Promise<void> {
    const discovery = await this.fetchWithOptionalRetry(
      () => fetchDiscovery(this.config.issuer)
    )

    if (this.tokens?.refresh_token) {
      try {
        await this.doFetch(discovery.revocation_endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            token: this.tokens.refresh_token,
            token_type_hint: "refresh_token",
            client_id: this.config.clientId,
          }).toString(),
        })
      } catch {
        // Best-effort revocation
      }
    }

    const idToken = this.tokens?.id_token
    this.clearTokens()
    this.sync?.emit("logout")

    const logoutParams = new URLSearchParams()
    if (idToken) logoutParams.set("id_token_hint", idToken)
    if (this.config.postLogoutRedirectUri) {
      logoutParams.set("post_logout_redirect_uri", this.config.postLogoutRedirectUri)
    }

    window.location.href = `${discovery.end_session_endpoint}?${logoutParams.toString()}`
  }

  async getAccessToken(): Promise<string | null> {
    if (!this.tokens) return null

    if (Date.now() >= this.tokens.expires_at - 30000) {
      const refreshed = await this.refreshToken()
      if (!refreshed) return null
    }

    return this.tokens.access_token
  }

  getTokens(): TokenSet | null {
    return this.tokens
  }

  isAuthenticated(): boolean {
    return this.tokens !== null && Date.now() < this.tokens.expires_at
  }

  isTokenExpired(bufferSeconds = 60): boolean {
    if (!this.tokens?.expires_at) return true
    return Date.now() >= this.tokens.expires_at - bufferSeconds * 1000
  }

  getIdTokenClaims(): IdTokenClaims | null {
    return this._idTokenClaims
  }

  getOrganization(): string | null {
    if (this.config.organization) return this.config.organization
    const claims = this._idTokenClaims
    if (claims && typeof claims.org_id === "string") return claims.org_id
    return null
  }

  onSync(handler: SyncHandler): void {
    if (this.sync) {
      // Replace the default handler with a custom one that also reloads tokens
      const oldSync = this.sync
      oldSync.destroy()
      this.sync = new AuthSync(`${this.storagePrefix}sync`, (event) => {
        this.loadTokens()
        handler(event)
      })
    }
  }

  destroy(): void {
    this.sync?.destroy()
    this.sync = null
  }

  // ---- Internal helpers ----

  private doFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (this.retryOptions) {
      return fetchWithRetry(input, init, { maxRetries: this.retryOptions.maxRetries })
    }
    return fetch(input, init)
  }

  private async fetchWithOptionalRetry<T>(fn: () => Promise<T>): Promise<T> {
    return fn()
  }

  private storageKey(name: string): string {
    return `${this.storagePrefix}${name}`
  }

  private saveTokens(): void {
    if (this.tokens) {
      this.storage.set(this.storageKey("tokens"), JSON.stringify(this.tokens))
    }
  }

  private loadTokens(): void {
    const scopedKey = this.storageKey("tokens")
    let stored = this.storage.get(scopedKey)
    const usesLegacyTokens = stored === null

    if (usesLegacyTokens) {
      stored = this.storage.get(LEGACY_STORAGE_KEYS.tokens)
    }

    if (!stored) return

    try {
      const parsed: TokenSet = JSON.parse(stored)
      if (usesLegacyTokens) {
        if (!this.legacyTokensMatchClient(parsed)) return

        // Claim the old global value once, then remove it so another client
        // cannot silently load the same token set.
        this.storage.set(scopedKey, stored)
        this.storage.remove(LEGACY_STORAGE_KEYS.tokens)
      }
      this.tokens = parsed
    } catch {
      // Invalid stored tokens are ignored.
    }
  }

  private legacyTokensMatchClient(tokens: TokenSet): boolean {
    // ID tokens are always JWTs; otherwise UniAuth access tokens provide the
    // issuer/audience proof needed to route a legacy global value safely.
    const claims = decodeJwtPayload(tokens.id_token || tokens.access_token)
    if (!claims || typeof claims.iss !== "string") return false

    const audiences = Array.isArray(claims.aud) ? claims.aud : [claims.aud]
    return normalizeIssuer(claims.iss) === this.config.issuer
      && audiences.includes(this.config.clientId)
  }

  private clearTokens(): void {
    this.tokens = null
    this._idTokenClaims = null
    this.storage.remove(this.storageKey("tokens"))
  }
}
